import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import api, { formatApiError, to12h, getInitials } from '../lib/api';
import { Calendar, Clock, MapPin, Mail, Loader2 } from 'lucide-react';

const statusColor = (status) => ({
  confirmed: 'bg-green-100 text-green-800',
  pending: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
  no_show: 'bg-orange-100 text-orange-800',
}[status] || 'bg-gray-100 text-gray-800');

const MyBookingsPage = ({ user }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = useCallback(() => {
    if (!user) { setLoading(false); return; }
    api.get('/bookings')
      .then((res) => setBookings(res.data))
      .catch((err) => toast({ title: 'Failed to load bookings', description: formatApiError(err), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [user, toast]);

  useEffect(() => { load(); }, [load]);

  const cancelBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/cancel`);
      toast({ title: 'Booking cancelled' });
      load();
    } catch (err) {
      toast({ title: 'Cancel failed', description: formatApiError(err), variant: 'destructive' });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="mybookings-signin">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Please sign in</h2>
          <p className="text-gray-600">Log in to view your bookings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 bg-gradient-to-br from-blue-50 to-cyan-50" data-testid="mybookings-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h1 className="text-4xl font-bold text-gray-900">My Bookings</h1>
          <p className="text-xl text-gray-600">Welcome back, {user?.name}! Here are your bookings.</p>
        </div>

        <Card className="mb-8 shadow-lg border-0 bg-white">
          <CardContent className="p-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {getInitials(user?.name)}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{user?.name}</h2>
                <div className="flex items-center space-x-4 text-gray-600 mt-2">
                  <span className="flex items-center"><Mail className="h-4 w-4 mr-2" />{user?.email}</span>
                  <Badge className="bg-blue-100 text-blue-800">Member</Badge>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600" data-testid="bookings-count">{bookings.length}</div>
                <div className="text-sm text-gray-500">Total Bookings</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-10 w-10 animate-spin text-blue-500" /></div>
        ) : bookings.length === 0 ? (
          <Card className="text-center py-12 shadow-lg border-0 bg-white" data-testid="mybookings-empty">
            <CardContent>
              <Calendar className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-6">Start your swimming journey with us!</p>
              <Button asChild className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700">
                <a href="/book-now">Make Your First Booking</a>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6" data-testid="mybookings-list">
            {bookings.map((booking) => (
              <Card key={booking.id} className="shadow-lg border-0 bg-white hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-4 gap-6 items-center">
                    <div className="md:col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-bold text-gray-900">{booking.service_name || booking.booking_type}</h3>
                        <div className="flex items-center gap-2">
                          {booking.payment_status === 'paid' && (
                            <Badge className="bg-emerald-100 text-emerald-800">Paid</Badge>
                          )}
                          <Badge className={statusColor(booking.status)}>
                            {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                          </Badge>
                        </div>
                      </div>
                      <div className="space-y-2 text-gray-600">
                        <div className="flex items-center"><Calendar className="h-4 w-4 mr-2" /><span>{new Date(booking.date).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span></div>
                        <div className="flex items-center"><Clock className="h-4 w-4 mr-2" /><span>{to12h(booking.start_time)}</span></div>
                        <div className="flex items-center"><MapPin className="h-4 w-4 mr-2" /><span>Hurupu Pools, BTM Layout</span></div>
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-sm text-gray-500 mb-1">Booking ID</div>
                      <div className="font-mono text-lg font-bold text-gray-900">{booking.booking_code}</div>
                      <div className="text-2xl font-bold text-blue-600 mt-2">{'\u20B9'}{(booking.amount || 0).toLocaleString('en-IN')}</div>
                    </div>
                    <div className="space-y-2">
                      {['confirmed', 'pending'].includes(booking.status) ? (
                        <Button variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50" onClick={() => cancelBooking(booking.id)} data-testid={`cancel-${booking.booking_code}`}>
                          Cancel Booking
                        </Button>
                      ) : (
                        <Button asChild className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700">
                          <a href="/book-now">Book Again</a>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBookingsPage;
